/**
 * Minimal history-based React router
 * Uses clean URLs: /login, /dashboard, etc.
 */

import { useState, useEffect, createContext, useContext, type ReactNode, type AnchorHTMLAttributes } from "react";

const RouterContext = createContext<string>("/");

export function Router({ children, initialPath }: { children: ReactNode; initialPath?: string }) {
  const [path, setPath] = useState(() => {
    if (initialPath) return initialPath;
    if (typeof window !== "undefined") return window.location.pathname;
    return "/";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      setPath(window.location.pathname);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return <RouterContext.Provider value={path}>{children}</RouterContext.Provider>;
}

export function Route({ path, component: Component }: { path: string; component: () => ReactNode }) {
  const currentPath = useContext(RouterContext);
  // Match path without query params
  const currentBase = currentPath.split("?")[0];
  if (currentBase !== path) return null;
  return <Component />;
}

export function Link({ href, children, onClick, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  return (
    <a
      href={href}
      {...props}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
            (props.target && props.target !== "_self") || props.download !== undefined) return;
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin || url.hash) return;
        e.preventDefault();
        navigate(url.pathname + url.search);
      }}
    >
      {children}
    </a>
  );
}

export function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
}

export function useRoute(): string {
  return useContext(RouterContext);
}

export function useSearchParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}
